import { pulsechain } from '../config/chains';

export interface EntityPageData {
  entityName: string;
  entityDescription: string;
  entityType: string;
  entityAddress: string;
}

// Get config values
const CHAIN_ID = pulsechain.id;
const CHAIN_ID_HEX = '0x' + CHAIN_ID.toString(16);
const RPC_URL = pulsechain.rpcUrls.default.http[0];

// Template content as string constants to avoid Vite parsing issues
const HTML_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{ENTITY_NAME}} - Entity Page</title>
    <link rel="stylesheet" href="./style.css">
    <!-- Web3.js Library -->
    <script src="./lib/web3.min.js"></script>
</head>
<body>
    <div class="entity-container">
        <!-- Header Section -->
        <header class="entity-header">
            <div class="entity-info">
                <h1 class="entity-name">{{ENTITY_NAME}}</h1>
                <p class="entity-description">{{ENTITY_DESCRIPTION}}</p>
                <div class="entity-meta">
                    <span class="entity-type">{{ENTITY_TYPE}}</span>
                    <span class="entity-address">{{ENTITY_ADDRESS}}</span>
                </div>
            </div>
            <div class="entity-images">
                <div class="main-image">
                    <img id="mainImage" src="" alt="Main Entity Image" onerror="this.style.display='none'">
                </div>
                <div class="image-gallery" id="imageGallery">
                    <!-- Images will be loaded dynamically -->
                </div>
            </div>
        </header>

        <!-- Properties Section -->
        <section class="entity-properties">
            <h2>Properties</h2>
            <div class="properties-grid" id="propertiesGrid">
                <!-- Properties will be loaded dynamically -->
            </div>
        </section>

        <!-- Products Section -->
        <section class="entity-products">
            <h2>Products</h2>
            <div class="products-grid" id="productsGrid">
                <!-- Products will be loaded dynamically -->
            </div>
        </section>
    </div>

    <script src="./script.js"></script>
</body>
</html>`;

const CSS_TEMPLATE = `* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #0f0f23;
    color: #ffffff;
    line-height: 1.6;
    min-height: 100vh;
}

.entity-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

/* Header Section */
.entity-header {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 30px;
    margin-bottom: 30px;
    position: relative;
    overflow: hidden;
    backdrop-filter: blur(10px);
}

.entity-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.05)"/><circle cx="50" cy="10" r="0.5" fill="rgba(255,255,255,0.05)"/><circle cx="10" cy="60" r="0.5" fill="rgba(255,255,255,0.05)"/><circle cx="90" cy="40" r="0.5" fill="rgba(255,255,255,0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
}

.entity-info {
    position: relative;
    z-index: 1;
    margin-bottom: 20px;
}

.entity-name {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 10px;
    color: #ffffff;
}

.entity-description {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 20px;
    max-width: 600px;
}

.entity-meta {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
}

.entity-type, .entity-address {
    background: rgba(255, 255, 255, 0.1);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.9rem;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.entity-address {
    font-family: 'Courier New', monospace;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
    border-color: rgba(59, 130, 246, 0.2);
}

/* Images Section */
.entity-images {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 20px;
    align-items: flex-start;
}

.main-image {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.main-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
}

.image-gallery {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 10px;
}

.gallery-image {
    width: 80px;
    height: 80px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;
    transition: transform 0.2s ease;
}

.gallery-image:hover {
    transform: scale(1.05);
}

.gallery-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
}

/* Properties Section */
.entity-properties {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 30px;
    margin-bottom: 30px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.entity-properties h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: #fff;
}

.properties-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
}

.property-item {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 15px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.property-key {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.property-value {
    font-size: 1rem;
    color: #fff;
    word-break: break-word;
}

/* Products Section */
.entity-products {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 30px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.entity-products h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 20px;
    color: #fff;
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
}

.product-card {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    backdrop-filter: blur(10px);
}

.product-card:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
}

.product-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.product-image-container {
    width: 48px;
    height: 48px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
}

.product-image-fallback {
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 600;
}

.product-info {
    flex: 1;
}

.product-name {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 4px;
    color: #fff;
}

.product-description {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 12px;
    line-height: 1.4;
}

.product-address {
    font-family: 'Courier New', monospace;
    font-size: 0.8rem;
    color: #3b82f6;
    background: rgba(59, 130, 246, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
    word-break: break-all;
    border: 1px solid rgba(59, 130, 246, 0.2);
}

/* Loading and Error States */
.loading {
    text-align: center;
    padding: 40px;
    color: rgba(255, 255, 255, 0.6);
}

.error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 8px;
    padding: 16px;
    color: #ef4444;
    margin: 20px 0;
}

.no-products, .no-properties {
    text-align: center;
    padding: 40px;
    color: rgba(255, 255, 255, 0.5);
    font-style: italic;
}

.wallet-required {
    text-align: center;
    padding: 40px;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 8px;
    color: #3b82f6;
    margin: 20px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .entity-header {
        padding: 20px;
    }
    
    .entity-name {
        font-size: 2rem;
    }
    
    .entity-meta {
        flex-direction: column;
        gap: 10px;
    }
    
    .entity-images {
        flex-direction: column;
        align-items: center;
    }
    
    .main-image {
        width: 100px;
        height: 100px;
    }
    
    .image-gallery {
        grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    }
    
    .gallery-image {
        width: 60px;
        height: 60px;
    }
    
    .products-grid {
        grid-template-columns: 1fr;
    }
    
    .properties-grid {
        grid-template-columns: 1fr;
    }
}`;

const JS_TEMPLATE = `// Entity Page JavaScript
class EntityPage {
    constructor() {
        this.entityAddress = '{{ENTITY_ADDRESS}}';
        this.web3 = null;
        this.contracts = {};
        this.init();
    }

    async init() {
        try {
            await this.setupWeb3();
            await this.loadEntityData();
            await this.loadProperties();
            await this.loadProducts();
        } catch (error) {
            console.error('Failed to initialize entity page:', error);
            this.showError('Failed to load entity data');
        }
    }

    async setupWeb3() {
        // Check if MetaMask is available
        if (typeof window.ethereum !== 'undefined') {
            this.web3 = new Web3(window.ethereum);
            try {
                await window.ethereum.request({ method: 'eth_requestAccounts' });
            } catch (error) {
                console.warn('User denied account access');
            }
        } else {
            // Fallback to a provider (you might want to use a public RPC)
            console.warn('MetaMask not found, using fallback provider');
            this.web3 = new Web3('http://localhost:8545');
        }

        try {
            // Hardcoded ABIs matching the working implementation
            const trinityEntityABI = [
                {
                    "inputs": [],
                    "name": "name",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "description",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "entityType",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "getProducts",
                    "outputs": [{ "internalType": "address[]", "name": "", "type": "address[]" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "properties",
                    "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
                    "stateMutability": "view",
                    "type": "function"
                }
            ];

            const trinityPropertiesABI = [
                {
                    "inputs": [{ "internalType": "address", "name": "entity", "type": "address" }],
                    "name": "getAllProperties",
                    "outputs": [
                        {
                            "components": [
                                { "internalType": "string", "name": "key", "type": "string" },
                                { "internalType": "uint8", "name": "valueType", "type": "uint8" },
                                { "internalType": "string", "name": "stringValue", "type": "string" },
                                { "internalType": "uint256", "name": "uintValue", "type": "uint256" },
                                { "internalType": "bool", "name": "boolValue", "type": "bool" },
                                { "internalType": "bytes", "name": "bytesValue", "type": "bytes" }
                            ],
                            "internalType": "struct Property[]",
                            "name": "",
                            "type": "tuple[]"
                        }
                    ],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "getUIImagesCount",
                    "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [{ "internalType": "uint8", "name": "index", "type": "uint8" }],
                    "name": "getUIImage",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [
                        { "internalType": "address", "name": "entity", "type": "address" },
                        { "internalType": "string", "name": "key", "type": "string" }
                    ],
                    "name": "getProperty",
                    "outputs": [
                        {
                            "components": [
                                { "internalType": "string", "name": "key", "type": "string" },
                                { "internalType": "uint8", "name": "valueType", "type": "uint8" },
                                { "internalType": "string", "name": "stringValue", "type": "string" },
                                { "internalType": "uint256", "name": "uintValue", "type": "uint256" },
                                { "internalType": "bool", "name": "boolValue", "type": "bool" },
                                { "internalType": "bytes", "name": "bytesValue", "type": "bytes" }
                            ],
                            "internalType": "struct Property",
                            "name": "",
                            "type": "tuple"
                        }
                    ],
                    "stateMutability": "view",
                    "type": "function"
                }
            ];

            // Initialize contracts
            this.contracts.trinityEntity = new this.web3.eth.Contract(trinityEntityABI, this.entityAddress);
            const propertiesAddress = await this.contracts.trinityEntity.methods.properties().call();
            this.contracts.trinityProperties = new this.web3.eth.Contract(trinityPropertiesABI, propertiesAddress);

            console.log('Contracts initialized:', this.contracts);
        } catch (error) {
            console.error('Failed to initialize contracts:', error);
            this.showError('Failed to initialize contracts: ' + error.message);
        }
    }

    async loadEntityData() {
        try {
            const [name, description, entityType] = await Promise.all([
                this.contracts.trinityEntity.methods.name().call(),
                this.contracts.trinityEntity.methods.description().call(),
                this.contracts.trinityEntity.methods.entityType().call()
            ]);

            // Update DOM elements
            document.querySelector('.entity-name').textContent = name || 'Unnamed Entity';
            document.querySelector('.entity-description').textContent = description || 'No description available';
            document.querySelector('.entity-type').textContent = entityType || 'Unknown Type';
            document.querySelector('.entity-address').textContent = this.entityAddress;

            // Load entity images
            await this.loadEntityImages();
        } catch (error) {
            console.error('Failed to load entity data:', error);
        }
    }

    async loadEntityImages() {
        try {
            const imageCount = await this.contracts.trinityProperties.methods.getUIImagesCount().call();
            const imageProperty = await this.contracts.trinityProperties.methods.getProperty(this.entityAddress, 'image').call();
            console.log('Image property:', imageProperty);  
            
            if (imageCount > 0 || imageProperty.stringValue) {
                // Load main image (first image)
                let mainImage = null;
                
                // Check if we have an image property with base64 data
                if (imageProperty.stringValue && imageProperty.stringValue.startsWith('/9j/')) {
                    // This is base64 JPEG data, convert to data URL
                    mainImage = 'data:image/jpeg;base64,' + imageProperty.stringValue;
                    console.log('Using base64 image data');
                } else if (imageCount > 0) {
                    // Fallback to UI images
                    mainImage = await this.contracts.trinityProperties.methods.getUIImage(0).call();
                    console.log('Using UI image');
                }
                
                if (mainImage) {
                    const mainImgElement = document.getElementById('mainImage');
                    mainImgElement.src = mainImage;
                    mainImgElement.style.display = 'block';
                }

                // Load gallery images
                const galleryContainer = document.getElementById('imageGallery');
                for (let i = 0; i < Math.min(imageCount, 8); i++) { // Limit to 8 images
                    const imageUrl = await this.contracts.trinityProperties.methods.getUIImage(i).call();
                    if (imageUrl) {
                        const galleryItem = document.createElement('div');
                        galleryItem.className = 'gallery-image';
                        galleryItem.innerHTML = \`<img src="\${imageUrl}" alt="Gallery Image \${i + 1}" onerror="this.parentElement.style.display='none'">\`;
                        galleryContainer.appendChild(galleryItem);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load entity images:', error);
        }
    }

    async loadProperties() {
        try {
            const properties = await this.contracts.trinityProperties.methods.getAllProperties(this.entityAddress).call();
            const propertiesGrid = document.getElementById('propertiesGrid');
            
            if (properties && properties.length > 0) {
                properties.forEach(property => {
                    if (property.key && property.valueType !== '0') { // Skip empty properties
                        const propertyItem = document.createElement('div');
                        propertyItem.className = 'property-item';
                        
                        let value = '';
                        switch (property.valueType) {
                            case '1': // STRING
                                value = property.stringValue;
                                break;
                            case '2': // UINT
                                value = property.uintValue;
                                break;
                            case '3': // BOOL
                                value = property.boolValue ? 'Yes' : 'No';
                                break;
                            case '4': // BYTES
                                value = '0x' + this.web3.utils.bytesToHex(property.bytesValue);
                                break;
                        }
                        
                        propertyItem.innerHTML = \`
                            <div class="property-key">\${property.key}</div>
                            <div class="property-value">\${value}</div>
                        \`;
                        propertiesGrid.appendChild(propertyItem);
                    }
                });
            } else {
                propertiesGrid.innerHTML = '<div class="no-properties">No properties found</div>';
            }
        } catch (error) {
            console.error('Failed to load properties:', error);
            document.getElementById('propertiesGrid').innerHTML = '<div class="error">Failed to load properties</div>';
        }
    }

    async loadProducts() {
        try {
            const products = await this.contracts.trinityEntity.methods.getProducts().call();
            const productsGrid = document.getElementById('productsGrid');
            
            if (products && products.length > 0) {
                for (const productAddress of products) {
                    await this.renderProduct(productAddress, productsGrid);
                }
            } else {
                productsGrid.innerHTML = '<div class="no-products">No products found</div>';
            }
        } catch (error) {
            console.error('Failed to load products:', error);
            document.getElementById('productsGrid').innerHTML = '<div class="error">Failed to load products</div>';
        }
    }

    async renderProduct(productAddress, container) {
        try {
            const trinityProductABI = [
                {
                    "inputs": [],
                    "name": "name",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },
                {
                    "inputs": [],
                    "name": "description",
                    "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
                    "stateMutability": "view",
                    "type": "function"
                },  
                {
                    "inputs": [],
                    "name": "properties",
                    "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
                    "stateMutability": "view",
                    "type": "function"
                }
            ];
            
            const productContract = new this.web3.eth.Contract(trinityProductABI, productAddress);
            
            const [name, description] = await Promise.all([
                productContract.methods.name().call(),
                productContract.methods.description().call()
            ]);

            // Get product image from properties
            let productImage = '';
            try {
                const productProperties = await this.contracts.trinityProperties.methods.getAllProperties(productAddress).call();
                const imageProperty = productProperties.find(p => p.key === 'image' || p.key === 'logo');
                if (imageProperty && imageProperty.stringValue) {
                    productImage = imageProperty.stringValue;
                }
            } catch (error) {
                console.warn('Failed to load product image:', error);
            }

            const productCard = document.createElement('div');
            productCard.className = 'product-card';
            
            productCard.innerHTML = \`
                <div class="product-header">
                    <div class="product-image-container">
                        \${productImage ? 
                            \`<img class="product-image" src="\${productImage}" alt="\${name}" onerror="this.parentElement.innerHTML='<div class=\\"product-image-fallback\\">\${name.charAt(0)}</div>'">\` :
                            \`<div class="product-image-fallback">\${name ? name.charAt(0) : 'P'}</div>\`
                        }
                    </div>
                    <div class="product-info">
                        <div class="product-name">\${name || 'Unnamed Product'}</div>
                        <div class="product-description">\${description || 'No description available'}</div>
                    </div>
                </div>
                <div class="product-address">\${productAddress}</div>
            \`;
            
            container.appendChild(productCard);
        } catch (error) {
            console.error('Failed to render product:', productAddress, error);
        }
    }

    showError(message) {
        const container = document.querySelector('.entity-container');
        const errorDiv = document.createElement('div');
        errorDiv.className = 'error';
        errorDiv.textContent = message;
        container.insertBefore(errorDiv, container.firstChild);
    }
}

// Initialize the page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new EntityPage();
});`;

export function generateEntityPageHTML(data: EntityPageData): string {
  let html = HTML_TEMPLATE;
  html = html.replace('{{ENTITY_NAME}}', data.entityName);
  html = html.replace('{{ENTITY_DESCRIPTION}}', data.entityDescription);
  html = html.replace('{{ENTITY_TYPE}}', data.entityType);
  html = html.replace('{{ENTITY_ADDRESS}}', data.entityAddress);
  
  return html;
}

export function generateEntityPageCSS(): string {
  return CSS_TEMPLATE;
}

export function generateEntityPageJS(entityAddress: string): string {
  console.log('=== GENERATING ENTITY PAGE JS ===');
  console.log('Entity Address:', entityAddress);
  console.log('Chain ID:', CHAIN_ID);
  console.log('Chain ID Hex:', CHAIN_ID_HEX);
  console.log('RPC URL:', RPC_URL);
  
  let js = JS_TEMPLATE;
  js = js.replace('{{ENTITY_ADDRESS}}', entityAddress);
  js = js.replace('{{CHAIN_ID_HEX}}', CHAIN_ID_HEX);
  js = js.replace('{{CHAIN_ID}}', CHAIN_ID.toString());
  js = js.replace('{{RPC_URL}}', RPC_URL);
  
  console.log('JS Template generated with entity address:', entityAddress);
  console.log('==========================================');
  return js;
}

export function generateEntityPageFiles(data: EntityPageData): { [key: string]: string } {
  // For frontend, we can't read the Web3.js file directly
  // The backend will handle reading the Web3.js library file
  
  // Generate separate files
  const htmlContent = generateEntityPageHTML(data);
  const cssContent = CSS_TEMPLATE;
  const jsContent = generateEntityPageJS(data.entityAddress);
  
  return {
    'index.html': htmlContent,
    'style.css': cssContent,
    'script.js': jsContent
    // Note: lib/web3.min.js will be added by the backend
  };
} 